import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import {
  ICON_SIZE,
  SHOW_TASK_BY_ORDER,
  SHOW_TASK_GROUPING_TYPE,
  TAB_BUTTON_TYPE,
  TASK_LIST_TYPE
} from '../../../global/constants';
import { isEmpty } from '../../../helpers/common';
import { AccountingData } from '../../../hooks/accounting/useAccountingData';
import { Dropdown, DropdownItem } from '../../dropdown';
import Tabs from '../../tabs';
import { Data, DropText, GroupDiv, Options, SVGDiv, TaskDrop, TaskFilterDiv } from '../styles';
import ChipMenuTrigger from './ChipMenuTrigger';

interface TaskFilterControlsProps {
  accounting: AccountingData;
}

const TaskFilterControls: React.FC<TaskFilterControlsProps> = observer(({ accounting }) => {
  const {
    currentProject,
    getGroupName,
    getOrderName,
    menus,
    onChangeGroupingFilter,
    onChangeOrderByFilter,
    onChangeTaskListType,
    taskFilter
  } = accounting;
  const groupMenuItems = [
    { key: SHOW_TASK_GROUPING_TYPE.NO_GROUPING, label: 'No Grouping' },
    currentProject?.isGroupEnabled && { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP, label: 'Group' },
    currentProject?.priority?.enabled && { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PRIORITY, label: 'Priority' },
    { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_USER, label: 'User' },
    currentProject?.isMilestone && { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_MILESTONE, label: 'Milestone' },
    currentProject?.statusEnable &&
      currentProject?.statusData?.length > 0 &&
      { key: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS, label: 'Status' }
  ].filter((item): item is { key: number; label: string } => Boolean(item));
  const orderingMenuItems = useMemo(() => {
    const items = [];
    if (
      (taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS ||
        taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP ||
        taskFilter?.groupingStatus === SHOW_TASK_GROUPING_TYPE.DEFAULT) &&
      taskFilter?.taskViewType !== TASK_LIST_TYPE.CALENDAR_VIEW
    ) {
      items.push({ key: SHOW_TASK_BY_ORDER.CUSTOM, label: 'Custom' });
    }
    if (currentProject?.priority?.enabled) {
      items.push({ key: SHOW_TASK_BY_ORDER.PRIORITY, label: 'Priority' });
    }
    if (taskFilter?.taskViewType !== TASK_LIST_TYPE.CALENDAR_VIEW) {
      items.push({ key: SHOW_TASK_BY_ORDER.DUE_DATE, label: 'Due Date' });
    }
    items.push({ key: SHOW_TASK_BY_ORDER.CREATED_DATE, label: 'Created Date' });
    return items;
  }, [currentProject?.priority?.enabled, taskFilter?.groupingStatus, taskFilter?.taskViewType]);
  const taskTabs = [
    {
      key: TASK_LIST_TYPE.TASKS,
      title: 'List',
      iconName: 'list-view-icon',
      isActive:
        taskFilter?.taskViewType === TASK_LIST_TYPE.TASKS || isEmpty(taskFilter?.taskViewType) || !taskFilter?.taskViewType
    },
    {
      key: TASK_LIST_TYPE.TASK_GRID,
      title: 'Grid',
      iconName: 'board-view-icon',
      isActive: taskFilter?.taskViewType === TASK_LIST_TYPE.TASK_GRID
    },
    {
      key: TASK_LIST_TYPE.CALENDAR_VIEW,
      title: 'Calendar',
      iconName: 'calendar-icon',
      isActive: taskFilter?.taskViewType === TASK_LIST_TYPE.CALENDAR_VIEW
    }
  ];

  return (
    <TaskDrop isTaskList>
      <Data>
        <Options>
          {taskTabs.map((tab) => (
            <Tabs
              key={tab.key}
              variant={TAB_BUTTON_TYPE.STANDARD}
              size='small'
              title={tab.title}
              onClick={() => onChangeTaskListType(tab.key)}
              isActive={tab.isActive}
              iconName={tab.iconName}
              iconPosition='start'
              applyFullWidth={true}
            />
          ))}
        </Options>
      </Data>
      <TaskFilterDiv>
        {taskFilter?.taskViewType !== TASK_LIST_TYPE.CALENDAR_VIEW && (
          <Data>
            <GroupDiv>
              <SVGDiv>
                <SVGIcon
                  name='updated-group-icon'
                  width={ICON_SIZE.SM}
                  height={ICON_SIZE.SM}
                  viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                  fill='var(--text-secondary)'
                />
              </SVGDiv>
              <DropText>Grouping</DropText>
            </GroupDiv>
            <Dropdown
              modalTitle='Grouping'
              content={
                <ChipMenuTrigger
                  title={getGroupName()}
                  isOpen={menus.isOpenGroupMenu}
                  onClick={() => menus.setIsOpenGroupMenu(!menus.isOpenGroupMenu)}
                />
              }
              trigger='click'
              onOutsideClick={() => menus.setIsOpenGroupMenu(false)}>
              {groupMenuItems.map((item) => (
                <DropdownItem
                  key={item.key}
                  label={item.label}
                  onClick={() => onChangeGroupingFilter(item.key)}
                  isActive={taskFilter?.groupingStatus === item.key}
                />
              ))}
            </Dropdown>
          </Data>
        )}
        <Data>
          <GroupDiv>
            <SVGDiv>
              <SVGIcon
                name='small-filters-icon'
                width={ICON_SIZE.SM}
                height={ICON_SIZE.SM}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                className='filters-icon-color'
              />
            </SVGDiv>
            <DropText>Ordering</DropText>
          </GroupDiv>
          <Dropdown
            modalTitle='Ordering'
            content={
              <ChipMenuTrigger
                title={getOrderName()}
                isOpen={menus.isOrderDropdownOpen}
                onClick={() => menus.setIsOrderDropdownOpen(!menus.isOrderDropdownOpen)}
              />
            }
            trigger='click'
            onOutsideClick={() => menus.setIsOrderDropdownOpen(false)}>
            {orderingMenuItems.map((item) => (
              <DropdownItem
                key={item.key}
                label={item.label}
                onClick={() => onChangeOrderByFilter(item.key)}
                isActive={taskFilter?.orderByStatus === item.key}
              />
            ))}
          </Dropdown>
        </Data>
      </TaskFilterDiv>
    </TaskDrop>
  );
});

export default TaskFilterControls;
